import { DownOutlined } from '@ant-design/icons';
import { Dropdown as AntdDropdown, Button, type MenuProps } from 'antd';
import type { FC, ReactNode } from 'react';

interface DropdownItemProps {
  onClick?: () => void;
  children: ReactNode;
  key?: string | number;
}

interface DropdownProps {
  trigger?: Array<'click' | 'hover' | 'contextMenu'>;
  placement?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'top' | 'topLeft' | 'topRight';
  children: ReactNode;
  label?: string;
  className?: string;
  menu?: MenuProps;
}

export const DropdownItem: FC<DropdownItemProps> = ({ onClick, children }) => {
  return (
    <div
      className="cursor-pointer px-3 py-2 hover:bg-gray-100"
      role="menuitem"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      {children}
    </div>
  );
};

const DropdownComponent: FC<DropdownProps> = ({
  trigger = ['click'],
  placement = 'bottomLeft',
  children,
  label = 'Actions',
  className = '',
  menu,
}) => {
  // If menu prop is provided, use it directly
  if (menu) {
    return (
      <AntdDropdown className={className} menu={menu} placement={placement} trigger={trigger}>
        <Button>
          {label} <DownOutlined />
        </Button>
      </AntdDropdown>
    );
  }

  // Convert children to menu items
  const menuItems = Array.isArray(children) ? children : [children];

  const items: MenuProps['items'] = menuItems.map((child: any, index: number) => ({
    key: child.key || index,
    label: child,
  }));

  return (
    <AntdDropdown className={className} menu={{ items }} placement={placement} trigger={trigger}>
      <Button>
        {label} <DownOutlined />
      </Button>
    </AntdDropdown>
  );
};

// Create the compound component with proper typing
interface DropdownCompoundProps extends FC<DropdownProps> {
  Item: FC<DropdownItemProps>;
}

const Dropdown = DropdownComponent as DropdownCompoundProps;
Dropdown.Item = DropdownItem;

export default Dropdown;
